import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOutletContext } from 'react-router-dom';
import Button from './Button';
import { fetchComments as apiFetchComments, postComment as apiPostComment } from '../api/client';

export default function CommentSection({ templateId }) {
  const { user, session } = useAuth();
  
  let setAuthOpen = null;
  try {
    const context = useOutletContext();
    setAuthOpen = context?.setAuthOpen;
  } catch (e) {}

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newComment, setNewComment] = useState('');
  const [promptType, setPromptType] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const loadComments = () => {
    setLoading(true);
    apiFetchComments(templateId)
      .then(data => {
        setComments(data.comments || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadComments();
  }, [templateId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await apiPostComment(templateId, { body: newComment, promptType });
      setNewComment('');
      setPromptType('general');
      loadComments();
    } catch (err) {
      console.error(err);
      alert('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const prompts = [
    { id: 'general', label: 'Write freely' },
    { id: 'use-case', label: 'I would use this for...' },
    { id: 'improvement', label: 'I would change...' }
  ];

  return (
    <div style={{ marginTop: '48px', borderTop: '1px solid var(--border-subtle)', paddingTop: '32px' }}>
      <h3 className="text-h3" style={{ marginBottom: '24px' }}>Community ({comments.length})</h3>

      <div style={{ marginBottom: '32px', backgroundColor: 'var(--bg-elevated)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {prompts.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setPromptType(p.id);
                if (p.id === 'use-case' && !newComment.includes('use this for')) setNewComment('I would use this for ');
                if (p.id === 'improvement' && !newComment.includes('change')) setNewComment('I would change ');
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: promptType === p.id ? 'var(--brand-primary)' : 'var(--bg-hover)',
                color: promptType === p.id ? '#fff' : 'var(--text-secondary)'
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <textarea 
            className="form-input" 
            placeholder="Join the discussion..." 
            style={{ minHeight: '80px', resize: 'vertical' }}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" type="submit" disabled={submitting || !newComment.trim()}>
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </form>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading comments...</div>
      ) : comments.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No comments yet. Be the first!</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: '16px' }}>
              <img src={c.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.user_id}`} alt={c.display_name} style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ fontWeight: 600 }}>{c.display_name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                {c.prompt_type !== 'general' && (
                  <div style={{ fontSize: '12px', color: 'var(--brand-primary)', marginBottom: '4px', fontWeight: 500 }}>
                    {c.prompt_type === 'use-case' ? '💡 Idea' : '🛠️ Feedback'}
                  </div>
                )}
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.body}</p>
                
                {/* Replies could go here */}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
